import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "../../lib/utils";

const pageContainerClasses =
  "min-h-screen w-full max-w-full overflow-x-hidden bg-background p-[var(--content-padding)] rtl max-md:p-3 max-[480px]:p-2";

const pageHeaderClasses =
  "mb-[var(--content-padding)] w-full rounded-[18px] bg-white px-8 py-6 shadow-[0_2px_4px_rgba(0,0,0,0.1)] max-lg:px-5 max-lg:py-[1.1rem] max-md:rounded-card max-md:p-4";

const pageTitleClasses =
  "m-0 text-3xl font-semibold leading-[1.35] text-slate-700 max-lg:text-[clamp(1.35rem,3vw,1.65rem)] max-[480px]:text-xl";

const pageContentClasses = "mx-auto w-full max-w-content max-w-full p-0";

const tabsContainerClasses =
  "max-w-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.05)] max-md:rounded-t-xl";

const tabsRowClasses =
  "flex max-w-full overflow-x-auto overflow-y-hidden border-b-2 border-slate-200 px-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] max-md:gap-0 max-md:px-2";

const tabContentClasses =
  "max-w-full overflow-x-hidden rounded-b-control bg-white p-8 min-h-[500px] shadow-[0_2px_4px_rgba(0,0,0,0.05)] max-lg:p-5 max-md:min-h-[320px] max-md:p-4 max-[480px]:p-3";

const placeholderClasses =
  "flex max-w-full min-h-[240px] items-center justify-center px-4 py-8 text-center text-xl text-slate-400 max-[480px]:min-h-[180px] max-[480px]:text-base";

function getTabButtonClasses(isActive) {
  return cn(
    "mb-[-2px] cursor-pointer whitespace-nowrap border-0 border-b-[3px] border-transparent bg-transparent px-8 py-4 text-base font-medium text-slate-500 transition-all duration-300",
    "hover:bg-slate-50 hover:text-slate-600",
    "max-lg:px-[1.35rem] max-lg:py-[0.9rem] max-md:px-4 max-md:py-[0.85rem] max-md:text-[0.92rem]",
    isActive && "text-primary border-b-primary",
  );
}

function groupTabs(tabs) {
  const groups = [];
  const index = new Map();
  tabs.forEach((tab) => {
    const name = tab.group || "";
    if (!index.has(name)) {
      index.set(name, groups.length);
      groups.push({ name, tabs: [] });
    }
    groups[index.get(name)].tabs.push(tab);
  });
  return groups;
}

function TabIcon({ icon, active }) {
  if (!icon) return null;
  if (typeof icon === "string") {
    return <span className="text-sm leading-none">{icon}</span>;
  }
  return (
    <span
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors",
        active
          ? "border-indigo-200 bg-white text-indigo-600"
          : "border-slate-200 bg-slate-50 text-slate-500",
      )}
    >
      {icon}
    </span>
  );
}

function VerticalTabButton({ tab, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-right transition-all duration-200",
        active
          ? "bg-indigo-50 text-indigo-800"
          : "text-slate-600 hover:bg-white/80 hover:text-slate-800",
      )}
    >
      <span
        className={cn(
          "absolute inset-y-1.5 right-0 w-0.5 rounded-full transition-opacity",
          active ? "bg-indigo-500 opacity-100" : "opacity-0 group-hover:opacity-40 bg-slate-300",
        )}
      />
      <TabIcon icon={tab.icon} active={active} />
      <span className={cn("min-w-0 flex-1 truncate text-[13px] font-medium", active && "text-indigo-900")}>
        {tab.label}
      </span>
    </button>
  );
}

function HorizontalTabs({ tabs, activeTab, setActiveTab }) {
  return (
    <div className={tabsContainerClasses}>
      <div className={tabsRowClasses}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={getTabButtonClasses(activeTab === tab.id)}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function VerticalNav({ tabs, activeTab, setActiveTab, query, setQuery }) {
  const showSearch = tabs.length > 6;
  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return tabs;
    return tabs.filter(
      (tab) =>
        (tab.label || "").includes(q) ||
        (tab.description || "").includes(q) ||
        String(tab.id || "").includes(q),
    );
  }, [tabs, query]);
  const groups = groupTabs(filtered);

  return (
    <aside className="flex w-full shrink-0 flex-col border-slate-200/80 bg-slate-50 md:w-[176px] md:border-l md:border-b-0">
      {showSearch ? (
        <div className="px-2 py-2">
          <label className="relative block">
            <Search className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="جستجو..."
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pr-8 pl-2 text-xs text-slate-700 outline-none placeholder:text-slate-400 focus:border-indigo-300"
            />
          </label>
        </div>
      ) : null}

      <nav className="flex gap-1 overflow-x-auto p-1.5 [-webkit-overflow-scrolling:touch] md:flex-col md:gap-2.5 md:overflow-y-auto md:overflow-x-hidden md:p-2">
        {groups.map((group) => (
          <div key={group.name || "default"} className="min-w-max md:min-w-0">
            {group.name ? (
              <p className="mb-0.5 hidden px-1.5 text-[10px] font-semibold text-slate-400 md:block">
                {group.name}
              </p>
            ) : null}
            <div className="flex gap-1 md:flex-col md:gap-0.5">
              {group.tabs.map((tab) => (
                <div key={tab.id} className="min-w-[132px] md:min-w-0">
                  <VerticalTabButton
                    tab={tab}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 ? (
          <p className="px-3 py-4 text-sm text-slate-400">موردی یافت نشد</p>
        ) : null}
      </nav>
    </aside>
  );
}

/**
 * Reusable tabbed page shell used across settings, reports, targets, etc.
 * Pass orientation="vertical" for a settings-style side rail.
 */
export default function TabbedPage({
  title,
  subtitle,
  tabs,
  defaultTab,
  placeholderText = "این بخش در حال توسعه است",
  orientation = "horizontal",
}) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);
  const [query, setQuery] = useState("");
  const activeTabDef = tabs.find((tab) => tab.id === activeTab);
  const ActiveComponent = activeTabDef?.component;
  const isVertical = orientation === "vertical";

  const content = typeof activeTabDef?.render === "function" ? (
    activeTabDef.render()
  ) : ActiveComponent ? (
    <ActiveComponent />
  ) : (
    <div className={placeholderClasses}>
      <p>{placeholderText}</p>
    </div>
  );

  return (
    <div className={pageContainerClasses}>
      <header className={cn(pageHeaderClasses, isVertical && "border border-indigo-50 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eef2ff_100%)]")}>
        <h1 className={pageTitleClasses}>{title}</h1>
        {subtitle ? (
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-500">{subtitle}</p>
        ) : null}
      </header>

      {isVertical ? (
        <div className={pageContentClasses}>
          <div className="overflow-hidden rounded-[22px] border border-slate-200/80 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col md:flex-row">
              <VerticalNav
                tabs={tabs}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                query={query}
                setQuery={setQuery}
              />
              <div className="min-w-0 flex-1 bg-white p-5 max-lg:p-4 max-md:min-h-[320px] max-md:p-4 max-[480px]:p-3">
                {activeTabDef?.label ? (
                  <div className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                    <TabIcon icon={activeTabDef.icon} active />
                    <h2 className="text-base font-semibold text-slate-800">
                      {activeTabDef.label}
                    </h2>
                  </div>
                ) : null}
                {content}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={pageContentClasses}>
          <HorizontalTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          <div className={tabContentClasses}>{content}</div>
        </div>
      )}
    </div>
  );
}
